/**
 * Driving-directions links for a pub.
 *
 * Builds deep links that hand a pub's coordinates to the user's maps app so
 * they can drive to it. Links are plain anchors, which keeps popups free of
 * event wiring and lets the browser/OS route them to the installed app.
 */

import type { Pub } from './types.js';

/** Maps providers we can hand a destination to. */
export type MapsProvider = 'apple' | 'google';

/** A ready-to-render directions link. */
export interface DirectionsLink {
  provider: MapsProvider;
  /** Button text, e.g. "Apple Maps" */
  label: string;
  /** Absolute https URL requesting driving directions */
  url: string;
}

const PROVIDER_LABELS: Record<MapsProvider, string> = {
  apple: 'Apple Maps',
  google: 'Google Maps',
};

/** Decimal places kept in deep links (~0.1 m precision). */
const COORD_PRECISION = 6;

/**
 * Formats a coordinate for use in a maps URL.
 * @param value - Latitude or longitude in decimal degrees
 * @returns Fixed-precision string without trailing zero noise
 */
function formatCoordinate(value: number): string {
  return String(Number(value.toFixed(COORD_PRECISION)));
}

/**
 * Checks that a pub carries usable coordinates.
 * @param pub - The pub object
 * @returns True when both lat and lng are finite and in range
 */
export function hasUsableCoordinates(pub: Pub): boolean {
  return (
    Number.isFinite(pub.lat) &&
    Number.isFinite(pub.lng) &&
    pub.lat >= -90 &&
    pub.lat <= 90 &&
    pub.lng >= -180 &&
    pub.lng <= 180
  );
}

/**
 * Builds an Apple Maps driving-directions URL.
 * `daddr` sets the destination, `dirflg=d` selects driving. The origin is
 * left out so Apple Maps uses the device's current location.
 * @param pub - The destination pub
 * @returns Apple Maps universal link
 */
export function buildAppleMapsUrl(pub: Pub): string {
  const destination = `${formatCoordinate(pub.lat)},${formatCoordinate(pub.lng)}`;
  return `https://maps.apple.com/?daddr=${encodeURIComponent(destination)}&dirflg=d`;
}

/**
 * Builds a Google Maps driving-directions URL.
 * Uses the documented Maps URLs API; omitting `origin` makes Google Maps
 * start from the user's current location.
 * @param pub - The destination pub
 * @returns Google Maps universal link
 */
export function buildGoogleMapsUrl(pub: Pub): string {
  const destination = `${formatCoordinate(pub.lat)},${formatCoordinate(pub.lng)}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    destination
  )}&travelmode=driving`;
}

/**
 * Detects whether a user agent belongs to an Apple platform.
 * iPadOS 13+ reports a Macintosh user agent, so that string counts too.
 * @param userAgent - navigator.userAgent value
 * @returns True for iOS/iPadOS/macOS
 */
export function prefersAppleMaps(userAgent: string): boolean {
  return /iphone|ipad|ipod|macintosh|mac os x/i.test(userAgent);
}

/**
 * Builds the ordered directions links for a pub.
 * Both providers are always offered; the platform's native app is listed
 * first so the common case is the first button.
 * @param pub - The destination pub
 * @param userAgent - navigator.userAgent value, used only for ordering
 * @returns Ordered links, or an empty array when the pub has no usable coordinates
 */
export function buildDirectionsLinks(pub: Pub, userAgent = ''): DirectionsLink[] {
  if (!hasUsableCoordinates(pub)) {
    return [];
  }

  const apple: DirectionsLink = {
    provider: 'apple',
    label: PROVIDER_LABELS.apple,
    url: buildAppleMapsUrl(pub),
  };
  const google: DirectionsLink = {
    provider: 'google',
    label: PROVIDER_LABELS.google,
    url: buildGoogleMapsUrl(pub),
  };

  return prefersAppleMaps(userAgent) ? [apple, google] : [google, apple];
}
